import { DateTime } from 'luxon'
import type { Dispatch } from 'react'
import { useReducer } from 'react'

import type { InsightRevenueDateTimeRange } from '@/features/site/types/revenue'

const MAX_REVENUE_RANGE_DAYS = 366

type UseRevenueFiltersParams = {
  limitDateTimeRange: InsightRevenueDateTimeRange
  availableMarketPrograms: string[]
  timezone: string
}

export type UseRevenueFiltersResult = [State, Dispatch<Actions>]

type State = {
  marketPrograms: string[]
  timeRange: InsightRevenueDateTimeRange
}

type Actions = SetMarketProgramsAction | SetTimeRangeAction

type SetMarketProgramsAction = {
  type: 'setMarketPrograms'
  payload: string[]
}

type SetTimeRangeAction = {
  type: 'setTimeRange'
  payload: InsightRevenueDateTimeRange
}

/**
 * Caps the initial time range to at most 366 days. The start time is anchored first,
 * then the end time is capped to prevent overloading the backend with large data requests.
 */
function capTimeRange(range: InsightRevenueDateTimeRange): InsightRevenueDateTimeRange {
  const latestAllowedEnd = range.startTime.plus({ days: MAX_REVENUE_RANGE_DAYS })

  return {
    startTime: range.startTime,
    endTime: DateTime.min(range.endTime, latestAllowedEnd),
  }
}

/**
 * The initial time range defaults to the current year, clamped within the available data range.
 */
function getDefaultTimeRange(
  limitDateTimeRange: InsightRevenueDateTimeRange,
  timezone: string,
): InsightRevenueDateTimeRange {
  const now = DateTime.now().setZone(timezone)
  const startOfYear = now.startOf('year')

  const startTime = DateTime.max(startOfYear, limitDateTimeRange.startTime)
  const endTime = DateTime.min(now, limitDateTimeRange.endTime)

  return capTimeRange({ startTime, endTime })
}

function getInitialState(
  limitDateTimeRange: InsightRevenueDateTimeRange,
  availableMarketPrograms: string[],
  timezone: string,
): State {
  return {
    marketPrograms: availableMarketPrograms,
    timeRange: getDefaultTimeRange(limitDateTimeRange, timezone),
  }
}

function reducer(state: State, action: Actions): State {
  switch (action.type) {
    case 'setMarketPrograms': {
      return {
        ...state,
        marketPrograms: action.payload,
      }
    }
    case 'setTimeRange': {
      return {
        ...state,
        timeRange: capTimeRange(action.payload),
      }
    }
    default: {
      return state
    }
  }
}

export function useRevenueFilters({ limitDateTimeRange, availableMarketPrograms, timezone }: UseRevenueFiltersParams) {
  return useReducer(reducer, null, () => getInitialState(limitDateTimeRange, availableMarketPrograms, timezone))
}
